import { Component, OnInit, inject, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { SearchService, SearchRequest, SearchResponse } from '../../services/search.service';
import { DateRangePickerComponent } from '../../shared/components/date-range-picker/date-range-picker.component';
import { toLocalISOString } from '../../core/utils/date.util';

interface AutocompleteResult {
  id: number;
  label: string;
  value: number;
  parentId: string;
  type: string;
  masterHotelId?: number; // Kıbrıs otelleri için MasterHotelId
}

interface AutocompleteResponse {
  results: AutocompleteResult[];
  errors: any[];
  total: number;
  types: { type: string; count: number; }[];
}

@Component({
  selector: 'app-search',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatInputModule,
    DateRangePickerComponent
  ],
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.scss']
})
export class SearchComponent implements OnInit {
  @Output() searchResults = new EventEmitter<SearchResponse>();
  @Output() searchError = new EventEmitter<string>();

  private readonly fb = inject(FormBuilder);
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly searchService = inject(SearchService);
  
  searchForm: FormGroup = this.fb.group({
    location: ['', Validators.required],
    checkInDate: [new Date(), Validators.required],
    checkOutDate: [new Date(), Validators.required],
    adults: [1, [Validators.required, Validators.min(1)]],
    children: [0, [Validators.required, Validators.min(0)]],
    rooms: [1, [Validators.required, Validators.min(1)]]
  });

  isCalendarOpen = false;
  isGuestSelectorOpen = false;
  filteredLocations$: Observable<AutocompleteResult[]>;
  selectedLocation: AutocompleteResult | null = null;
  loading = false;

  constructor() {
    this.filteredLocations$ = this.searchForm.get('location')!.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(term => this.searchLocations(term))
    );
  }

  ngOnInit(): void {
    this.initializeDates();
    
    // Takvim kapatma işlemi kaldırıldı - artık sadece tarih seçildiğinde kapanacak
  }

  private initializeDates(): void {
    const checkIn = new Date();
    checkIn.setDate(checkIn.getDate() + 1);
    
    const checkOut = new Date();
    checkOut.setDate(checkOut.getDate() + 8);
    
    this.searchForm.patchValue({
      checkInDate: checkIn,
      checkOutDate: checkOut
    });
  }

  private searchLocations(term: string): Observable<AutocompleteResult[]> {
    if (!term || term.length < 2) {
      return new Observable(subscriber => subscriber.next([]));
    }

    const url = `${environment.apiUrl}/Autocomplete/autocomplete`;
    const params = {
      term: term,
      lang: 'tr',
      limit: '5',
      type: 'all'
    };

    return this.http.get<AutocompleteResponse>(url, { params, withCredentials: true }).pipe(
      map(response => response.results)
    );
  }

  toggleCalendar(): void {
    this.isCalendarOpen = !this.isCalendarOpen;
    if (this.isCalendarOpen) {
      this.isGuestSelectorOpen = false;
    }
  }

  closeCalendar(): void {
    this.isCalendarOpen = false;
  }

  onDateRangeSelected(event: { startDate: Date; endDate: Date }): void {
    this.searchForm.patchValue({
      checkInDate: event.startDate,
      checkOutDate: event.endDate
    });
    this.isCalendarOpen = false;
  }

  toggleGuestSelector(): void {
    this.isGuestSelectorOpen = !this.isGuestSelectorOpen;
    if (this.isGuestSelectorOpen) {
      this.isCalendarOpen = false;
    }
  }

  updateGuestCount(type: 'adults' | 'children' | 'rooms', increment: boolean): void {
    const control = this.searchForm.get(type);
    if (control) {
      const currentValue = control.value;
      const newValue = increment ? currentValue + 1 : currentValue - 1;
      
      if (newValue >= 0) {
        control.setValue(newValue);
      }
    }
  }

  onLocationSelect(location: AutocompleteResult): void {
    this.selectedLocation = location;
    this.searchForm.patchValue({ location: location.label });
  }

  performSearch(): void {
    if (this.searchForm.valid && this.selectedLocation) {
      this.loading = true;
      
      // SearchService'i sıfırla (yeni arama için)
      this.searchService.resetSearch();

      const checkInDate = new Date(this.searchForm.get('checkInDate')?.value);
      checkInDate.setHours(0, 0, 0, 0);

      const checkOutDate = new Date(this.searchForm.get('checkOutDate')?.value);
      checkOutDate.setHours(0, 0, 0, 0);

      // Kıbrıs oteli veya Hotel tipinde ise özel işlem
      const isCyprusOrHotel = this.selectedLocation.type?.toLowerCase() === 'cyprus' || 
                              this.selectedLocation.type?.toLowerCase() === 'hotel';
      
      // Kıbrıs oteli için masterHotelId varsa onu kullan, yoksa id'yi kullan
      const hotelIdValue = isCyprusOrHotel 
        ? (this.selectedLocation.masterHotelId || this.selectedLocation.id)
        : 0;

      const searchRequest: SearchRequest = {
        currency: "TRY",
        checkInDate: toLocalISOString(checkInDate),
        checkOutDate: toLocalISOString(checkOutDate),
        countryCode: "TR",
        destinations: [{
          destinationId: isCyprusOrHotel ? 0 : this.selectedLocation.id,
          hotelId: isCyprusOrHotel ? hotelIdValue : 0,
          destinationLabel: this.selectedLocation.label,
          destinationParentId: isCyprusOrHotel ? 0 : (parseInt(this.selectedLocation.parentId) || 0),
          locationType: isCyprusOrHotel ? 5 : this.getLocationType(this.selectedLocation.type) // Kıbrıs için 5 (Hotel)
        }],
        pageNumber: 1,
        pageSize: 10,
        rooms: this.createRooms(),
        includeRoomPrices: false,
      };

      // SearchService üzerinden arama yap
      this.searchService.searchHotels(searchRequest)
        .subscribe({
          next: (response: SearchResponse) => {
            this.searchResults.emit(response as any);

            // Her durumda hotel-list'e git (alternatif oda varsa orada uyarı gösterilir)
            this.searchService.handleSearchResponse(response);
            this.loading = false;
          },
          error: (error) => {
            this.searchError.emit('Arama yapılırken bir sorun oluştu. Lütfen tekrar deneyin.');
            this.loading = false;
          }
        });
    }
  }

  private getLocationType(type: string): number {
    if (!type) return 0;
    switch (type.toLowerCase()) {
      case 'city':
        return 0;
      case 'country':
        return 1;
      case 'continent':
        return 2;
      case 'district':
        return 3;
      case 'area':
        return 4;
      case 'hotel':
      case 'cyprus': // Kıbrıs otelleri de Hotel tipi olarak işlenir
        return 5;
      case 'category':
        return 6;
      default:
        return 0;
    }
  }

  private createRooms(): { passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] }[] {
    const rooms: { passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] }[] = [];
    const adults = this.searchForm.get('adults')?.value || 0;
    const children = this.searchForm.get('children')?.value || 0;
    const passengers: { age: number; birthday: string | null; gender: number | null; passengerType: number }[] = [];

    // Yetişkin yolcular
    for (let i = 0; i < adults; i++) {
      passengers.push({
        age: 0, // Yetişkinler için yaş 0 gönderilir
        birthday: null,
        gender: null,
        passengerType: 0 // 0 = Adult
      });
    }
    
    // Çocuk yolcular
    for (let i = 0; i < children; i++) {
      const childAge = 5; // Default çocuk yaşı
      passengers.push({
        age: childAge,
        birthday: null,
        gender: null,
        passengerType: 1 // 1 = Child
      });
    }

    rooms.push({ passengers });
    return rooms;
  }
} 